import OfflineQueue from './offline';
import Batcher from './batcher';
import uuid from './uuid';
import consume from './consumer';

import {Event, PAGE, Properties, TransportFormat} from './types';

import {getTraceId, setTraceId} from './trace';

const legacyEventLabel = "legacy_event";
const virtualPageViewLabel = "virtual_pageview";

interface DeviceInfo {
    [s: string]: string;
}

interface MetaInfo {
    applicationName?: string;
    sdkName: string;
    sdkVersion: string;
}

export interface Hit {
    originalTimestamp: number;
    uuid: string;
    payload: Event;
}

export interface Bulk {
    sentAt: number;
    uuid: string;
    deviceInfo: DeviceInfo;
    metaInfo: MetaInfo;
    traceId?: string;
    hits: Hit[];
}

interface TpConfig {
    endpoint: string;
    storage?: any;
    key?: string;
    batchTimeout?: number;
    window?: Window | any;
    document?: Document | any;
    navigator?: Navigator | any;

}

export default class TpClient {
    private endpoint: string;
    public offlineQueue: OfflineQueue;
    public batcher: Batcher;
    private $w: Window;
    private $d: Document;
    private $n: Navigator;

    private isInUnload: boolean;
    private deviceInfo: DeviceInfo;
    private metaInfo: MetaInfo;
    private viewState: {
        uuid: string;
        url: string;
        referrer: string;
        lastUrl: string;
        counter: number;
    };

    public constructor(config: TpConfig) {
        this.endpoint = config.endpoint;
        this.offlineQueue = new OfflineQueue(config.storage, config.key);
        this.batcher = new Batcher(this.hitHandler.bind(this), config.batchTimeout || 1000);

        this.$w = config.window || window;
        this.$d = config.document || document;
        this.$n = config.navigator || navigator;

        this.isInUnload = false;
        const unloadHandler = this.unloadHandler.bind(this);
        this.$w.addEventListener('beforeunload', unloadHandler, false);
        this.$w.addEventListener('unload', unloadHandler, false);

        // super simple offline support, not cross-browser-tested, but default to be opt-in
        this.updateOnlineStatus();
        this.$w.addEventListener(
            'online',
            () => this.updateOnlineStatus(),
            false
        );

        this.deviceInfo = {
            screenWidth: String(this.$w.screen.width),
            screenHeight: String(this.$w.screen.height),
            width: String(this.$w.innerWidth),
            height: String(this.$w.innerHeight),
            language: String(this.$n.language),
            timeOffset: String(new Date().getTimezoneOffset() * -1), // Convert offset to make it relative to UTC.
        };

        this.metaInfo = {
            sdkName: 'web-js',
            sdkVersion: `PACKAGE_VERSION`,
            applicationName: undefined,
        };

        this.viewState = {
            uuid: uuid(),
            url: '',
            referrer: this.$d && this.$d.referrer ? this.$d.referrer : '',
            lastUrl: this.$d.location.href,
            counter: 0,
        };

        window.__tpClientHook = window.__tpClientHook || [];
        window.tpDataLayer = window.tpDataLayer || [];

        consume((entry: TransportFormat) => {
            const [type, beacon, trackNow] = entry;
            switch (type) {
                case PAGE:
                    this.trackPage(beacon, trackNow);
                    return;
                default:
                    this.track(beacon, trackNow);
                    return;
            }
        });
    }

    public setApplication(applicationName: string) {
        this.metaInfo.applicationName = applicationName;
    }

    private unloadHandler() {
        this.isInUnload = true;
        this.batcher.send();
    }

    private hitHandler(hits: any[]) {
        if (OfflineQueue.hasOnlineSupport && !this.$n.onLine) {
            hits.forEach(hit => this.offlineQueue.append(hit));
            this.emitHookEvent('offline-queued', hits);
            return;
        }

        const bulk: Bulk = {
            sentAt: Date.now(),
            uuid: uuid(),
            deviceInfo: this.deviceInfo,
            metaInfo: this.metaInfo,
            hits,
        };

        const traceId = getTraceId();
        if (traceId) {
            bulk.traceId = traceId;
        }
        this.emitHookEvent('bulk', bulk);
        this.sendBeacon(JSON.stringify(bulk));
    }

    private emitHookEvent(hookEventName: string, data: Bulk | Hit | Hit[]) {
        window.__tpClientHook.forEach(cb => {
            try {
                cb(hookEventName, JSON.parse(JSON.stringify(data)));
            } catch (e) {
                window.console.error('Hook failed', e);
            }
        });
    }

    private updateOnlineStatus() {
        if (this.$n.onLine) {
            // process and requeue queued-up-items
            this.offlineQueue.processQueueAndEmpty((hit: Hit) => {
                this.batcher.queue(hit);
            });
        }
    }

    private prepareBeacon(b: Event) {
        if (!b) {
            throw new Error('Tracking payload missing');
        }

        if (!b.type) {
            b.type = 'event';
        }

        if (b.name && typeof b.name !== 'string') {
            b.name = String(b.name);
        }

        if (b.type && typeof b.type !== 'string') {
            b.type = String(b.type);
        }

        const referrer = this.viewState.referrer;
        if (referrer) {
            b.referrer = referrer;
        }

        if (!b.viewUuid) {
            b.viewUuid = this.viewState.uuid;
        }

        const url = this.viewState.url;
        if (url) {
            b.url = url;
        } else {
            // We have no current state, use browser URL directly.
            b.url = this.$d.location.href;
        }

        return {
            ...b,
        };
    }

    private xhrFallback(data: string) {
        const xhr = new XMLHttpRequest();
        const useAsync = !this.isInUnload;
        xhr.open('POST', this.endpoint, useAsync); // TODO: always async?
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'text/json');
        try {
            xhr.send(data);
        } catch (e) {
            if (window.console) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        }
        return true;
    }

    private sendBeacon(data: string) {
        if (this.$n.sendBeacon) {
            return this.$n.sendBeacon(this.endpoint, data);
        }
        this.xhrFallback(data);
    }

    public enableTraceId(overrideUuid?: string) {
        return setTraceId(true, overrideUuid);
    }

    public disableTraceId() {
        return setTraceId(false);
    }

    public trackPage(data?: Event, trackNow: boolean = false) {
        data = this.addDataLayer(data);

        const state = this.viewState;
        if (state.counter >= 1) {
            let isValid = true;

            if (data) {
                // Treat as a virtual page view if url is present.
                state.url = data.url ? data.url : this.$d.location.href;
            } else {
                state.url = this.$d.location.href;
            }

            /* This will protect against multiple trackPage() calls on the same url. */
            if (this.batcher.buffer.length > 0) {
                const pageViews = this.batcher.buffer
                    .filter(hit => hit.payload.type === 'page-view');

                if (pageViews.length > 0) {
                    isValid = !(pageViews[pageViews.length - 1].payload.url === state.url);
                }
            }

            if (state.lastUrl !== state.url) {
                // Document URL has changed since last trackPage call
                state.referrer = state.lastUrl;
                state.lastUrl = state.url;
            }

            if (isValid) {
                state.uuid = uuid();
                state.counter++;
                this.track({type: 'page-view', ...data}, trackNow);
            } else if (trackNow) {
                this.batcher.send();
            }

        } else {
            state.counter++;
            this.track({type: 'page-view', ...data}, trackNow);
        }
    }

    private addDataLayer(event?: Event) {
        const dataLayerProperties = this.extractPropertiesFromDataLayer();

        if (event != null) {
            if (event.properties != null) {
                // Event.properties override dataLayer.properties
                event.properties = Object.assign(dataLayerProperties, event.properties);
            } else if (Object.keys(dataLayerProperties).length > 0){
                event.properties = dataLayerProperties;
            }
        } else if (Object.keys(dataLayerProperties).length > 0) {
            event = { 'properties': dataLayerProperties };
        }
        return event;
    }

    private extractPropertiesFromDataLayer(): Properties {
        const dataLayer = window.tpDataLayer;

        return dataLayer
            .filter(value => value['properties'] != null)
            .map(value => value['properties'])
            .filter(properties => Object.keys(properties).length > 0)
            .reduce((previousValue, currentValue) => Object.assign(previousValue, currentValue), {}) as Properties;
    }

    public trackVirtualPage(virtualUrl: string, data: Event, trackNow: boolean = false) {
        data.url = virtualUrl;

        const labels: string[] = data.labels ? [...data.labels, virtualPageViewLabel] : [virtualPageViewLabel];

        this.trackPage({...data, labels: labels}, trackNow);
    }

    public track(data: Event, trackNow: boolean = false) {
        const hit: Hit = {
            originalTimestamp: Date.now(),
            uuid: uuid(),
            payload: this.prepareBeacon(data),
        };
        this.emitHookEvent('queue', hit);
        this.batcher.queue(hit);
        if (trackNow) {
            this.batcher.send();
        }

        this.appendToDataLayer(hit);
    }

    private appendToDataLayer(hit: Hit) {
        if (window.tpDataLayer) {
            // @ts-ignore
            window.tpDataLayer.push({ 'enqueued_hit': hit });
        }
    }
}
